import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { ConnectorResponse } from "@/api";
import {
  createConnectorMutation,
  getConnectorDetailQueryKey,
  listConnectorsQueryKey,
  listConnectorToolsQueryKey,
  updateConnectorMutation,
} from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";
import { connectorFormSchema, toCreateBody, toUpdateBody, toValues } from "./connectorValues";
import { EndpointFields } from "./EndpointFields";
import { HeadersEditor } from "./HeadersEditor";
import { SettingsEditor } from "./SettingsEditor";

/** The connector Overview form: the create page and the detail tab share it. Editors+ edit. */
export function ConnectorForm({ connector }: Readonly<{ connector?: ConnectorResponse }>) {
  const { t } = useTranslation("connectors");
  const navigate = useNavigate();
  const text = formText(t, t);
  const { saveExisting, createNew } = useEntitySave(text.messages);
  const create = useMutation(createConnectorMutation());
  const builtin = connector?.transport === "builtin";
  const update = useMutation(updateConnectorMutation());

  const form = useAppForm({
    defaultValues: toValues(connector),
    // An integration's connector has no URL to validate.
    validators: builtin ? undefined : { onChange: connectorFormSchema(t) },
    onSubmit: ({ value }) => {
      if (connector) {
        const path = { connector_uuid: connector.uuid };
        return saveExisting({
          request: () => update.mutateAsync({ path, body: toUpdateBody(value, builtin), throwOnError: true }),
          invalidate: [
            listConnectorsQueryKey(),
            getConnectorDetailQueryKey({ path }),
            listConnectorToolsQueryKey({ path }),
          ],
          onSaved: () => form.reset(value),
        });
      }
      return createNew({
        request: async () => (await create.mutateAsync({ body: toCreateBody(value), throwOnError: true })).data.uuid,
        invalidate: [listConnectorsQueryKey()],
        to: (uuid) => `/build/connectors/${uuid}`,
      });
    },
  });

  return (
    <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
      <IdentityFields form={form} fields={{ name: "name", description: "description" }} labels={text.labels} />
      {!builtin && (
        <>
          <EndpointFields form={form} creating={!connector} />
          <HeadersEditor form={form} />
          <SettingsEditor form={form} />
        </>
      )}
      <form.AppForm>
        <form.SubmitActions
          onCancel={() => (connector ? form.reset() : navigate("/build/connectors"))}
          submitLabel={connector ? undefined : t("form.create")}
          requireChanges={Boolean(connector)}
        />
      </form.AppForm>
    </EntityFormCard>
  );
}
